// Project 8: 토큰 관리 모달
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { publishService } from '@/services/PublishService';
import type { PublishPlatform, TokenSaveRequest } from '@/types/publish';

interface TokenModalProps {
  platform: PublishPlatform;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function TokenModal({ platform, isOpen, onClose, onSuccess }: TokenModalProps) {
  const [displayName, setDisplayName] = useState('');
  
  // WordPress
  const [siteUrl, setSiteUrl] = useState('');
  const [username, setUsername] = useState('');
  const [appPassword, setAppPassword] = useState('');
  
  // Tistory
  const [tistoryAccessToken, setTistoryAccessToken] = useState('');
  const [tistoryRefreshToken, setTistoryRefreshToken] = useState('');
  const [blogName, setBlogName] = useState('');
  const [tistoryClientId, setTistoryClientId] = useState('');
  const [tistoryClientSecret, setTistoryClientSecret] = useState('');
  const [redirectUri, setRedirectUri] = useState('');
  
  // Naver
  const [naverAccessToken, setNaverAccessToken] = useState('');
  const [naverRefreshToken, setNaverRefreshToken] = useState('');
  const [naverClientId, setNaverClientId] = useState('');
  const [naverClientSecret, setNaverClientSecret] = useState('');

  const saveMutation = useMutation({
    mutationFn: (request: TokenSaveRequest) => publishService.saveToken(request),
    onSuccess: () => {
      alert('토큰이 저장되었습니다.');
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    let credentials: any = {};

    if (platform === 'wordpress') {
      if (!siteUrl || !username || !appPassword) {
        alert('모든 필드를 입력해주세요.');
        return;
      }
      credentials = { username, appPassword };
    } else if (platform === 'tistory') {
      if (!tistoryAccessToken || !blogName) {
        alert('Access Token과 Blog Name을 입력해주세요.');
        return;
      }
      credentials = {
        accessToken: tistoryAccessToken,
        refreshToken: tistoryRefreshToken,
        blogName,
        clientId: tistoryClientId,
        clientSecret: tistoryClientSecret,
        redirectUri
      };
    } else if (platform === 'naver') {
      if (!naverAccessToken || !naverClientId || !naverClientSecret) {
        alert('Access Token, Client ID, Client Secret을 입력해주세요.');
        return;
      }
      credentials = {
        accessToken: naverAccessToken,
        refreshToken: naverRefreshToken,
        clientId: naverClientId,
        clientSecret: naverClientSecret
      };
    }

    saveMutation.mutate({
      platform,
      displayName: displayName || platform.charAt(0).toUpperCase() + platform.slice(1),
      credentials,
      siteUrl: platform === 'wordpress' ? siteUrl : undefined
    });
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 8,
        padding: '2rem',
        maxWidth: '500px',
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ margin: 0 }}>{platform} 토큰 설정</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#999'
            }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                표시 이름
              </label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={platform.charAt(0).toUpperCase() + platform.slice(1)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: 6
                }}
              />
            </div>

            {platform === 'wordpress' && (
              <>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                    사이트 URL *
                  </label>
                  <input
                    type="url"
                    value={siteUrl}
                    onChange={(e) => setSiteUrl(e.target.value)}
                    placeholder="https://example.com"
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: 6
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                    사용자명 *
                  </label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: 6
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                    Application Password *
                  </label>
                  <input
                    type="password"
                    value={appPassword}
                    onChange={(e) => setAppPassword(e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: 6
                    }}
                  />
                  <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>
                    WordPress 관리자 → 사용자 → 프로필 → Application Passwords에서 생성
                  </div>
                </div>
              </>
            )}

            {platform === 'tistory' && (
              <>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                    Access Token *
                  </label>
                  <input
                    type="text"
                    value={tistoryAccessToken}
                    onChange={(e) => setTistoryAccessToken(e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: 6
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                    Blog Name *
                  </label>
                  <input
                    type="text"
                    value={blogName}
                    onChange={(e) => setBlogName(e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: 6
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                    Refresh Token
                  </label>
                  <input
                    type="text"
                    value={tistoryRefreshToken}
                    onChange={(e) => setTistoryRefreshToken(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: 6
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                    Client ID
                  </label>
                  <input
                    type="text"
                    value={tistoryClientId}
                    onChange={(e) => setTistoryClientId(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: 6
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                    Client Secret
                  </label>
                  <input
                    type="password"
                    value={tistoryClientSecret}
                    onChange={(e) => setTistoryClientSecret(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: 6
                    }}
                  />
                </div>
              </>
            )}

            {platform === 'naver' && (
              <>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                    Access Token *
                  </label>
                  <input
                    type="text"
                    value={naverAccessToken}
                    onChange={(e) => setNaverAccessToken(e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: 6
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                    Client ID *
                  </label>
                  <input
                    type="text"
                    value={naverClientId}
                    onChange={(e) => setNaverClientId(e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: 6
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                    Client Secret *
                  </label>
                  <input
                    type="password"
                    value={naverClientSecret}
                    onChange={(e) => setNaverClientSecret(e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: 6
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                    Refresh Token
                  </label>
                  <input
                    type="text"
                    value={naverRefreshToken}
                    onChange={(e) => setNaverRefreshToken(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #ddd',
                      borderRadius: 6
                    }}
                  />
                </div>
              </>
            )}

            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
              <button
                type="submit"
                disabled={saveMutation.isPending}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: saveMutation.isPending ? '#ccc' : '#2196f3',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  cursor: saveMutation.isPending ? 'not-allowed' : 'pointer',
                  fontWeight: 600
                }}
              >
                {saveMutation.isPending ? '저장 중...' : '저장'}
              </button>
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: '#f0f0f0',
                  color: '#333',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer'
                }}
              >
                취소
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

